import React, { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Mail, Phone, MapPin, Calendar, Download, Loader2, Eye, EyeOff, Search } from "lucide-react";
import { API } from "../lib/api";
import { findUniversity } from "../data/universities";

const TOKEN_KEY = "ecpprofili_admin_token";

export function AdminLogin() {
  const [token, setToken] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (localStorage.getItem(TOKEN_KEY)) navigate("/admin/richieste", { replace: true });
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Verify token by calling the protected endpoint
      await axios.get(`${API}/contacts`, { headers: { "X-Admin-Token": token } });
      localStorage.setItem(TOKEN_KEY, token);
      toast.success("Accesso effettuato");
      navigate("/admin/richieste");
    } catch (err) {
      if (err?.response?.status === 401) toast.error("Token non valido");
      else toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] pt-32 pb-20 flex items-center justify-center px-5 bg-brand-surface" data-testid="page-admin-login">
      <div className="w-full max-w-md">
        <div className="rounded-3xl bg-white border border-brand-line shadow-xl p-8">
          <div className="w-14 h-14 rounded-2xl bg-brand-navy text-white flex items-center justify-center mb-5">
            <Lock className="w-6 h-6" />
          </div>
          <h1 className="font-heading font-extrabold text-2xl text-brand-navy">Area riservata</h1>
          <p className="text-brand-muted text-sm mt-1">Accedi con il token amministratore per visualizzare le richieste di contatto.</p>

          <form onSubmit={submit} className="mt-7 space-y-4">
            <div>
              <label className="block text-[11px] font-bold tracking-[0.18em] uppercase text-brand-navy mb-1.5">Token admin</label>
              <div className="relative">
                <input
                  type={show ? "text" : "password"}
                  required
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  className="w-full h-12 px-4 pr-11 rounded-xl border border-brand-line bg-white text-brand-ink font-mono text-sm focus:outline-none focus:ring-2 focus:ring-brand-emerald focus:border-brand-emerald"
                  placeholder="Inserisci il token"
                  data-testid="admin-token-input"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-muted hover:text-brand-navy"
                  aria-label="Mostra/nascondi token"
                >
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="admin-login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Accedi
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export function AdminDashboard() {
  const [token] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterType, setFilterType] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) return;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/contacts?limit=500`, { headers: { "X-Admin-Token": token } });
        setItems(res.data || []);
      } catch (err) {
        if (err?.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          navigate("/admin");
        } else toast.error("Errore caricamento richieste");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, navigate]);

  if (!token) return <Navigate to="/admin" replace />;

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    navigate("/admin");
  };

  const filtered = items.filter((it) => {
    if (filterType !== "all" && it.type !== filterType) return false;
    if (q) {
      const s = q.toLowerCase();
      return (
        it.nome?.toLowerCase().includes(s) ||
        it.cognome?.toLowerCase().includes(s) ||
        it.email?.toLowerCase().includes(s) ||
        it.telefono?.includes(s) ||
        it.corso?.toLowerCase().includes(s) ||
        it.residenza?.toLowerCase().includes(s)
      );
    }
    return true;
  });

  const exportCsv = () => {
    const header = ["Data", "Tipo", "Nome", "Cognome", "Email", "Telefono", "Residenza", "Università", "Corso", "Modalità", "Messaggio"];
    const rows = filtered.map((it) => [
      it.created_at,
      it.type,
      it.nome,
      it.cognome,
      it.email,
      it.telefono,
      it.residenza,
      findUniversity(it.universita)?.short || it.universita,
      it.corso,
      it.modalita_iscrizione || "",
      (it.messaggio || "").replace(/[\r\n]+/g, " "),
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c || "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ecp-profili-richieste-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const stats = {
    total: items.length,
    contact: items.filter((i) => i.type === "contact").length,
    enrollment: items.filter((i) => i.type === "enrollment").length,
    newsletter: items.filter((i) => i.type === "newsletter").length,
  };

  return (
    <div className="min-h-[80vh] pt-28 pb-20 bg-brand-surface" data-testid="page-admin-dashboard">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="eyebrow text-brand-emerald mb-1">Area riservata</div>
            <h1 className="font-heading font-extrabold text-3xl lg:text-4xl text-brand-navy">Richieste di contatto</h1>
          </div>
          <div className="flex gap-2">
            <button onClick={exportCsv} className="btn-secondary text-sm" data-testid="admin-export-csv">
              <Download className="w-4 h-4" /> Esporta CSV
            </button>
            <button onClick={logout} className="text-sm font-bold text-brand-muted hover:text-brand-navy px-4 py-2.5 rounded-full border border-brand-line bg-white" data-testid="admin-logout">
              <LogOut className="w-4 h-4 inline mr-1.5" /> Esci
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-5 mb-6">
          <StatCard label="Totale" value={stats.total} color="navy" />
          <StatCard label="Informazioni" value={stats.contact} color="emerald" />
          <StatCard label="Iscrizioni" value={stats.enrollment} color="gold" />
          <StatCard label="Newsletter" value={stats.newsletter} color="muted" />
        </div>

        {/* Filters */}
        <div className="rounded-2xl bg-white border border-brand-line p-3 lg:p-4 flex flex-wrap gap-2 mb-5">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-muted" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca per nome, email, corso..."
              className="w-full h-11 pl-10 pr-4 rounded-full border border-brand-line bg-brand-surface text-sm focus:outline-none focus:ring-2 focus:ring-brand-emerald"
              data-testid="admin-search"
            />
          </div>
          {[
            { id: "all", label: "Tutte" },
            { id: "contact", label: "Informazioni" },
            { id: "enrollment", label: "Iscrizioni" },
            { id: "newsletter", label: "Newsletter" },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilterType(f.id)}
              className={`px-4 h-11 rounded-full text-sm font-semibold transition-all ${
                filterType === f.id ? "bg-brand-navy text-white" : "bg-brand-surface text-brand-ink"
              }`}
              data-testid={`admin-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20 text-brand-muted">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-brand-muted bg-white rounded-2xl border border-brand-line" data-testid="admin-empty">
            Nessuna richiesta corrisponde ai filtri.
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-requests-list">
            {filtered.map((it) => (
              <RequestCard key={it.id} item={it} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  const colors = {
    navy: "text-brand-navy",
    emerald: "text-brand-emerald",
    gold: "text-brand-gold",
    muted: "text-brand-muted",
  };
  return (
    <div className="rounded-2xl bg-white border border-brand-line p-5">
      <div className="text-xs uppercase tracking-wider font-semibold text-brand-muted">{label}</div>
      <div className={`font-heading font-extrabold text-3xl mt-1 ${colors[color]}`}>{value}</div>
    </div>
  );
}

function RequestCard({ item }) {
  const uni = findUniversity(item.universita);
  const typeColor = {
    contact: "bg-emerald-100 text-emerald-900",
    enrollment: "bg-amber-100 text-amber-900",
    newsletter: "bg-violet-100 text-violet-900",
  }[item.type] || "bg-brand-surface text-brand-ink";
  const typeLabel = { contact: "Informazioni", enrollment: "Iscrizione", newsletter: "Newsletter" }[item.type] || item.type;
  return (
    <div className="rounded-2xl bg-white border border-brand-line p-5 lg:p-6 hover:shadow-md transition-shadow" data-testid={`admin-request-${item.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <span className={`text-[10px] font-bold tracking-wider uppercase px-2 py-1 rounded-full ${typeColor}`}>{typeLabel}</span>
          <span className="text-xs text-brand-muted inline-flex items-center gap-1.5">
            <Calendar className="w-3 h-3" /> {new Date(item.created_at).toLocaleString("it-IT")}
          </span>
        </div>
        <div className="font-heading font-bold text-base text-brand-navy">{item.nome} {item.cognome}</div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
        <a href={`mailto:${item.email}`} className="inline-flex items-center gap-2 text-brand-ink hover:text-brand-emerald break-all">
          <Mail className="w-4 h-4 text-brand-emerald shrink-0" /> {item.email}
        </a>
        <a href={`tel:${item.telefono}`} className="inline-flex items-center gap-2 text-brand-ink hover:text-brand-emerald">
          <Phone className="w-4 h-4 text-brand-emerald shrink-0" /> {item.telefono}
        </a>
        <div className="inline-flex items-center gap-2 text-brand-muted">
          <MapPin className="w-4 h-4 text-brand-emerald shrink-0" /> {item.residenza}
        </div>
      </div>
      {item.type !== "newsletter" && (
        <div className="mt-4 pt-4 border-t border-brand-line grid sm:grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-xs text-brand-muted uppercase font-semibold tracking-wider">Università</div>
            <div className="font-semibold text-brand-navy mt-0.5">{uni?.short || item.universita}</div>
          </div>
          <div>
            <div className="text-xs text-brand-muted uppercase font-semibold tracking-wider">Corso</div>
            <div className="font-semibold text-brand-navy mt-0.5">{item.corso}</div>
          </div>
          {item.modalita_iscrizione && (
            <div className="sm:col-span-2">
              <div className="text-xs text-brand-muted uppercase font-semibold tracking-wider">Modalità iscrizione</div>
              <div className="font-semibold text-brand-navy mt-0.5">{item.modalita_iscrizione}</div>
            </div>
          )}
        </div>
      )}
      {item.messaggio && (
        <div className="mt-4 pt-4 border-t border-brand-line">
          <div className="text-xs text-brand-muted uppercase font-semibold tracking-wider mb-1">Messaggio</div>
          <p className="text-sm text-brand-ink leading-relaxed">{item.messaggio}</p>
        </div>
      )}
    </div>
  );
}
